import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);
const DEMO_SESSION_KEY = 'tcm_demo_user';

function isLocalDev() {
  const host = window.location.hostname;
  const isPrivateNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(host);
  return process.env.NODE_ENV === 'development' && (['localhost', '127.0.0.1'].includes(host) || isPrivateNetwork);
}

function getLocalDemoUser() {
  if (!isLocalDev()) return null;
  try {
    return JSON.parse(sessionStorage.getItem(DEMO_SESSION_KEY) || 'null');
  } catch {
    return null;
  }
}

export function calcularVencimento(inicioPlano, plano) {
  if (!inicioPlano || !plano) return null;
  const dias = { mensal: 30, trimestral: 90, semestral: 180 }[plano] || 30;
  const venc = new Date(inicioPlano);
  venc.setDate(venc.getDate() + dias);
  return venc;
}

export function diasParaVencer(inicioPlano, plano) {
  const venc = calcularVencimento(inicioPlano, plano);
  if (!venc) return null;
  const hoje = new Date(); hoje.setHours(0,0,0,0);
  return Math.ceil((venc - hoje) / (1000 * 60 * 60 * 24));
}

async function callAdminFunction(action, payload, accessToken) {
  const res = await fetch('/.netlify/functions/admin-manage-aluna', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ action, ...payload }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Falha na operação administrativa.');
  return body;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getLocalDemoUser());
  const [alunas, setAlunas] = useState([]);
  const [loading, setLoading] = useState(true);

  const carregarAlunas = useCallback(async () => {
    const { data, error } = await supabase.from('alunas').select('*').order('name');
    if (!error && data) {
      setAlunas(data.map(a => ({
        id: a.id, name: a.name, email: a.email,
        role: a.role || 'aluna', telefone: a.telefone, status: a.status,
        plano: a.plano, inicioPlano: a.inicio_plano,
        acessoTreino: a.acesso_treino !== false, acessoDieta: a.acesso_dieta !== false,
        genero: a.genero || 'aluna',
      })));
    }
  }, []);

  const buildUserFromSession = useCallback(async (session) => {
    if (!session) return null;
    const role = session.user.app_metadata?.role;
    if (role === 'admin') {
      return {
        id: session.user.id,
        name: session.user.user_metadata?.name || session.user.email,
        role: 'admin',
        cargo: session.user.user_metadata?.cargo || 'Administradora',
        email: session.user.email,
      };
    }
    const alunaId = session.user.app_metadata?.aluna_id;
    if (!alunaId) return null;
    const { data: aluna } = await supabase.from('alunas').select('*').eq('id', alunaId).maybeSingle();
    if (!aluna) return null;
    return {
      id: aluna.id, name: aluna.name, email: aluna.email,
      role: 'aluna', telefone: aluna.telefone, status: aluna.status,
      plano: aluna.plano, inicioPlano: aluna.inicio_plano,
      acessoTreino: aluna.acesso_treino !== false, acessoDieta: aluna.acesso_dieta !== false,
      genero: aluna.genero || 'aluna',
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function syncUser(session) {
      if (!session) {
        const demoUser = getLocalDemoUser();
        if (demoUser) {
          if (!active) return;
          setUser(demoUser);
          setLoading(false);
          return;
        }
      }
      const builtUser = await buildUserFromSession(session);
      if (!active) return;
      setUser(builtUser);
      if (builtUser) await carregarAlunas();
      else setAlunas([]);
    }

    supabase.auth.getSession().then(({ data }) => {
      syncUser(data.session).finally(() => { if (active) setLoading(false); });
    });

    // Supabase's auth callback must return synchronously — awaiting inside it
    // can deadlock the client's internal lock against getSession(). Defer instead.
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setTimeout(() => { if (active) syncUser(session); }, 0);
    });

    return () => { active = false; listener.subscription.unsubscribe(); };
  }, [buildUserFromSession, carregarAlunas]);

  async function login(email, password) {
    let res;
    try {
      res = await fetch('/.netlify/functions/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      return { success: false, error: 'Não foi possível conectar. Tente novamente.' };
    }
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: body.error || 'Email ou senha incorretos.' };

    const { error: sessionError } = await supabase.auth.setSession({
      access_token: body.access_token,
      refresh_token: body.refresh_token,
    });
    if (sessionError) return { success: false, error: 'Email ou senha incorretos.' };

    const { data: { session } } = await supabase.auth.getSession();
    const builtUser = await buildUserFromSession(session);
    if (!builtUser) {
      await supabase.auth.signOut();
      return { success: false, error: 'Email ou senha incorretos.' };
    }
    if (builtUser.role === 'aluna' && builtUser.status === 'pausada') {
      await supabase.auth.signOut();
      return { success: false, error: 'Seu acesso está pausado. Entre em contato com o Team Carol Macedo.' };
    }
    setUser(builtUser);
    await carregarAlunas();
    return { success: true, user: builtUser };
  }

  async function loginDemo(role = 'aluna') {
    if (!isLocalDev()) return { success: false, error: 'Acesso demo disponível apenas no servidor local.' };
    const demoUser = role === 'admin'
      ? {
        id: 'demo-admin',
        name: 'Carol Demo',
        role: 'admin',
        cargo: 'Administradora',
        email: 'admin.demo@teamcarol.local',
      }
      : {
        id: 'demo-aluna',
        name: 'Erika Demo',
        role: 'aluna',
        email: 'aluna.demo@teamcarol.local',
        status: 'ativa',
        plano: 'mensal',
        inicioPlano: new Date().toISOString().split('T')[0],
        acessoTreino: true,
        acessoDieta: true,
        genero: 'aluna',
      };
    setUser(demoUser);
    sessionStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(demoUser));
    return { success: true, user: demoUser };
  }

  async function logout() {
    sessionStorage.removeItem(DEMO_SESSION_KEY);
    await supabase.auth.signOut();
    setUser(null);
    setAlunas([]);
  }

  async function cadastrarAluna(dados) {
    const { data: { session } } = await supabase.auth.getSession();
    const result = await callAdminFunction('create', {
      name: dados.name, email: dados.email, password: dados.password || 'team123',
      telefone: dados.telefone || null, plano: dados.plano || 'mensal',
      inicioPlano: dados.inicioPlano || new Date().toISOString().split('T')[0],
      acessoTreino: dados.acessoTreino !== false,
      acessoDieta: dados.acessoDieta !== false,
      genero: dados.genero || 'aluna',
    }, session.access_token);
    await carregarAlunas();
    return result;
  }

  async function atualizarAluna(id, dados) {
    if (dados.email !== undefined) {
      // Trocar e-mail precisa sincronizar com o Supabase Auth (login),
      // não só a tabela — passa pela function que faz isso com service role.
      const { data: { session } } = await supabase.auth.getSession();
      const result = await callAdminFunction('update', { alunaId: id, ...dados }, session.access_token);
      await carregarAlunas();
      return result;
    }
    const update = {};
    if (dados.name) update.name = dados.name;
    if (dados.telefone !== undefined) update.telefone = dados.telefone;
    if (dados.status) update.status = dados.status;
    if (dados.plano) update.plano = dados.plano;
    if (dados.inicioPlano) update.inicio_plano = dados.inicioPlano;
    if (dados.acessoTreino !== undefined) update.acesso_treino = dados.acessoTreino;
    if (dados.acessoDieta !== undefined) update.acesso_dieta = dados.acessoDieta;
    if (dados.genero) update.genero = dados.genero;
    await supabase.from('alunas').update(update).eq('id', id);
    await carregarAlunas();
  }

  async function pausarAluna(id) { await atualizarAluna(id, { status: 'pausada' }); }
  async function reativarAluna(id) { await atualizarAluna(id, { status: 'ativa' }); }

  async function excluirAluna(id) {
    const { data: { session } } = await supabase.auth.getSession();
    await callAdminFunction('delete', { alunaId: id }, session.access_token);
    await carregarAlunas();
  }

  return (
    <AuthContext.Provider value={{ user, login, loginDemo, logout, alunas, loading, cadastrarAluna, atualizarAluna, pausarAluna, reativarAluna, excluirAluna, isLocalDev: isLocalDev() }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
